import { DesignContextProvider } from "Components/DesignContext";
import { ContactUsForm, ContactUsSchema, FormType } from "Components/Modal/ContactUsModal";
import { useForm } from "Hooks";
import { Controller, FieldErrors } from "react-hook-form";
import { PageBase } from "Pages/PageBase";
import React, { useEffect, useState } from "react";
import { render } from "react-dom";
import { Provider, useSelector } from "react-redux";
import "Styles/Pages/LeadCaptureLP.scss";
import assertContainerElement from "Utils/assertContainerElement";
import { userSelector } from "Utils/selectors";
import { yupResolver } from "@hookform/resolvers/yup";
import { EmailInputFL, InputFL, SelectInputFL, ValidationSummary } from "Components/Inputs";
import { CompanySize, EnterpriseClient, EnterpriseDemoRequest, EnterpriseDemoRequestMode, InterestReason } from "Services/API/API";
import { SpinningButton } from "Components/ReactstrapSlim";
import ReCAPTCHA from "react-google-recaptcha";
import { trackLeadCaptureForm } from "Utils/gaTrackers";
import { useCookies } from "react-cookie";
import { useApiClient } from "Services/API/useApiClient";
import { DefaultConfirmation } from "./Comfirmations/DefaultConfirmation";
import { PortfoilioConfirmation } from "./Comfirmations/PortfolioConfirmation";
import classNames from "classnames";
import useMediaBreakpointDown from "Hooks/useMediaBreakpointDown";

const FIND_DATA = {
    title: "Find workspace–by the hour, day, month or longer.",
    list: [
        {
            order: 1,
            boldText: "Browse and search:",
            text: "Filter space by location, space type, capacity, amenities, price, and more."
        },
        {
            order: 2,
            boldText: "Book it:",
            text: "Secure your space with your preferred payment method."
        },
        {
            order: 3,
            boldText: "Access it:",
            text: "Keep an eye out for your confirmation details–it's that easy!"
        }
    ],
    action: {
        href: "/search",
        text: "Find Space Now"
    }
}

const LIST_DATA = {
    title: "Join the largest flexible office marketplace.",
    list: [
        {
            order: 1,
            boldText: "Build your profile:",
            text: "Create a profile online, add space details, then set prices and availability."
        },
        {
            order: 2,
            boldText: "Publish your listings:",
            text: "Validate your listings on LiquidSpace Marketplace."
        },
        {
            order: 3,
            boldText: "Manage bookings:",
            text: "Effortlessly control communications and calendars through your venue manager portal."
        }
    ],
    action: {
        href: "/share-office-meeting-space",
        text: "List Space Now"
    }
}

const root = document.getElementById("LeadCaptureRoot");
assertContainerElement(root);

const LeadCapturePage: React.FC = () => {
    const user = useSelector(userSelector);
    const enteripseClient = useApiClient(EnterpriseClient);

    const [serverError, setServerError] = useState<Array<[string, string]>>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);

    const [cookies] = useCookies(["hubspotutk"]);
    const isMobile = useMediaBreakpointDown("md");

    const formMethods = useForm<ContactUsForm>({
        mode: "onTouched",
        resolver: yupResolver(ContactUsSchema),
        defaultValues: {
            formType: FormType.Specific,
            email: user?.email ?? undefined,
            fullName: user?.fullName ?? undefined,
        }
    });

    const formValues = formMethods.watch();

    const submit = async () => {
        setIsSubmitting(true);
        try {
            const isValid = await formMethods.trigger();
            if (isValid) {
                const requestData = formMethods.getValues();
                const { formType, ...request } = requestData;
  
                const demoRequest = EnterpriseDemoRequest.fromJS({
                    mode: EnterpriseDemoRequestMode.LC,
                    ...request,
                    hubspotUTK: cookies.hubspotutk,
                    referrerQuery: window.location.search
                });
  
                const requestResult = await enteripseClient.requestDemo(demoRequest);
  
                if (requestResult.success) {
                    setShowSuccessScreen(true);
                    window.scrollTo(0, 0);
                } else {
                    setServerError([["requestDemo", requestResult.errorMessage || "Unexpected server error"]]);
                }
                trackLeadCaptureForm(EnterpriseDemoRequestMode.LC, "submit");
            } else {
                let name: keyof FieldErrors<ContactUsForm>;
                for (name in formMethods.formState.errors) {
                    formMethods.setFocus(name);
                    break;
                }
            }
        } catch (err: any) {
            if (err.error) {
                setServerError([["requestDemo", err.error]]);
            }
            console.log(err);
        } finally {
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        ContactUsSchema.isValid(formValues).then(isValid => setSubmitButtonDisabled(!isValid));
    }, [formValues.company, formValues.companySize, formValues.interestReason, formValues.email, formValues.fullName, formValues.phoneNumber, formValues.solution, formValues.question, formValues.formType, formValues.recaptchaToken]);

    useEffect(() => {
        window.history.pushState(null, "", window.location.href);
        const handlePopState = () => {
            window.location.reload();
        };
        
        window.addEventListener("popstate", handlePopState);

        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    return <DesignContextProvider name="marketing">
        <PageBase><div className={classNames({"leadcapturelp-gradient-line": !showSuccessScreen || formValues.interestReason == InterestReason.Other || formValues.interestReason == InterestReason.Demo })}>
            <div className="container">
                <div className={classNames("leadcapturelp-container", { "d-none" : showSuccessScreen })}>
                    <div className="leadcapturelp-info">
                        <h2 className="leadcapturelp-title" dangerouslySetInnerHTML={{ __html: isMobile ? "Let’s connect–<br/> in just a few clicks." : "See what LiquidSpace can do for you." }}></h2>
                        <div className="leadcapturelp-desc">Beyond being the largest global marketplace for flexible office space, LiquidSpace is transforming the way hybrid workplaces are managed. Our platform oversees your entire portfolio leveraging AI and real-time data to drive smarter, more strategic workplace decisions. Plus, our advanced social tools motivate in-person collaboration, making it easier for employees to connect and be productive.</div>
                        <div className="leadcapturelp-subtitle">Trusted by Leading Enterprises</div>
                        <div className="leadcapturelp-logos">
                            <div className="leadcapturelp-logos-row">
                                <div className="leadcapturelp-logo t-mobile-logo"></div>
                                <div className="leadcapturelp-logo penn-mutual-logo"></div>
                                <div className="leadcapturelp-logo gsa-logo"></div>
                                <div className="leadcapturelp-logo philips-logo"></div>
                            </div>
                            <div className="leadcapturelp-logos-row">
                                <div className="leadcapturelp-logo hinge-health-logo"></div>
                                <div className="leadcapturelp-logo gofundme-logo"></div>
                                <div className="leadcapturelp-logo smartsheet-logo"></div>
                                <div className="leadcapturelp-logo arcadis-logo"></div>
                            </div>
                        </div>
                    </div>
                    <div className="leadcapturelp-form">
                        <div>
                            <InputFL control={formMethods.control} name="formType" className="d-none" hidden />
                            <InputFL control={formMethods.control} name="fullName" placeholder="Full Name" aria-label="full name" bsSize="lg" className="rfl-48" readOnly={!!user?.email} onDark />
                            <EmailInputFL control={formMethods.control} name="email" placeholder="Email Address" aria-label="email address" bsSize={"lg"} className="rfl-48" autoComplete="off" readOnly={!!user?.fullName} onDark />
                            <InputFL control={formMethods.control} name="phoneNumber" placeholder="Phone (Optional)" aria-label="phone(optional)" bsSize="lg" className="rfl-48" onDark />
                            <InputFL control={formMethods.control} name="company" placeholder="Company" aria-label="company" bsSize="lg" className="rfl-48" onDark autoComplete="off"/>
                            <SelectInputFL control={formMethods.control} name="companySize" placeholder="Company Size" aria-label="company size" bsSize="lg" className="rfl-48" onDark autoComplete="off">
                                <option value={undefined} aria-label="none" className="d-none"></option>
                                <option value={CompanySize.Small}>1-99 employees</option>
                                <option value={CompanySize.Medium}>100-499 employees</option>
                                <option value={CompanySize.Large}>500-4,999 employees</option>
                                <option value={CompanySize.ExtraLarge}>5,000+ employees</option>
                            </SelectInputFL>
                            <SelectInputFL control={formMethods.control} name="interestReason" placeholder="I want to" aria-label="i want to" bsSize="lg" className="rfl-48" onDark autoComplete="off">
                                <option value={undefined} aria-label="none" className="d-none"></option>
                                <option value={InterestReason.Book}>Book - Find workspace for myself or my team</option>
                                <option value={InterestReason.Demo}>Demo - Enterprise solutions for flexible space and hybrid work</option>
                                <option value={InterestReason.List}>List - Monetize my workspaces on LiquidSpace</option>
                                <option value={InterestReason.Other}>Other</option>
                            </SelectInputFL>
                            <InputFL control={formMethods.control} name="question" placeholder="Notes (Optional)" aria-label="Notes (Optional)" type="textarea" bsSize="lg" className="rfl-48" onDark autoComplete="off" />
                            <div className="leadcapturelp-actions">
                                <div className="leadcapturelp-captcha">
                                    <Controller
                                        control={formMethods.control}
                                        name="recaptchaToken"
                                        render={({ field, fieldState }) =>
                                        <>
                                            <ReCAPTCHA
                                                sitekey={LiquidSpaceConfig.RecaptchaKeys.Explicit.SiteKey!}
                                                onChange={token => field.onChange(token || undefined)}
                                                onExpired={() => field.onChange("")}
                                            />
                                            {
                                                <div className="field-validation-error">{fieldState.error?.message}</div>
                                            }
                                        </>
                                    } />
                                </div>
                                <div className="leadcapturelp-action-btn">
                                    <SpinningButton color="primary-on-dark" onClick={submit} isLoading={isSubmitting} aria-disabled={submitButtonDisabled} size="md" fluid="sm">Get in touch</SpinningButton>
                                </div>
                            </div>
                            <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverError} />
                        </div>
                    </div>
                </div>
                <div className={classNames("leadcapturelp-confirmations", { "d-none": !showSuccessScreen })}>
                    { formValues.interestReason == InterestReason.Book && <DefaultConfirmation data={FIND_DATA} /> }
                    { formValues.interestReason == InterestReason.List && <DefaultConfirmation data={LIST_DATA} /> }
                    { (formValues.interestReason == InterestReason.Demo || formValues.interestReason == InterestReason.Other) && <PortfoilioConfirmation /> }
                </div>
            </div></div>
        </PageBase>
    </DesignContextProvider>
}

render(<Provider store={LSStore.instance}>
    <LeadCapturePage />
</Provider>, root);
